import { useMemo } from 'react';
import { startCase } from 'lodash';
import FilterDropdown from 'components/elements/Dropdown/FilterDropdown';
import { useAvailableCities } from 'hooks/useUserCityAccess';
import {
  useCityIds,
  useUserCountySelection,
  useUserMultiRegionSelection,
} from 'state/dashboard';

const LocationDropdown = () => {
  const [selectedCityIds, setSelectedCityIds] = useCityIds();
  const [selectedRegions] = useUserMultiRegionSelection();
  const [selectedCounties] = useUserCountySelection();
  const { availableRegions } = useAvailableCities();

  const cityOptions = useMemo(() => {
    if (!selectedRegions || selectedRegions.length === 0) {
      return [];
    }

    // Collect all cities from all selected regions.
    // If counties are selected, only include cities from those counties.
    const allCities = selectedRegions
      .flatMap((regionCode) => {
        const region = availableRegions[regionCode];
        if (!region) return [];
        if (!selectedCounties || selectedCounties.length === 0) {
          return region.cities || [];
        }

        return selectedCounties.flatMap(
          (countyId) => region.counties?.[countyId]?.cities || [],
        );
      })
      // Remove duplicates based on city id
      .filter((city, index, self) => index === self.findIndex((c) => c.id === city.id));

    return allCities.map(({ id, name }) => ({
      value: id,
      label: startCase(name.toLowerCase()),
    }));
  }, [availableRegions, selectedRegions, selectedCounties]);

  return (
    <FilterDropdown
      options={cityOptions}
      onSubmit={(values: string[]) => {
        // onSubmit assumes it will always return a list of strings
        setSelectedCityIds(values.map((stringValue) => parseInt(stringValue, 10)));
      }}
      onClearAll={() => setSelectedCityIds([])}
      checkedValues={selectedCityIds}
      setCheckedValues={setSelectedCityIds}
      maxHeight="280px"
      isFilterEnabled
      sortSelectedFirst
    >
      City/Town
    </FilterDropdown>
  );
};

export default LocationDropdown;
